import { useCallback, useEffect, useMemo, useState } from 'react';
import { Hexagon, Loader2, RefreshCw, Sparkles, ZoomIn } from 'lucide-react';

import { KnowledgeParticleEmbeddedImageSection } from '../../components/KnowledgeParticleEmbeddedImageSection';
import { KnowledgeParticleOpsMoreMenu } from '../../components/KnowledgeParticleOpsMoreMenu';
import { KnowledgeParticlePapPanel } from '../../components/KnowledgeParticlePapPanel';
import { KnowledgeParticleQaSections } from '../../components/KnowledgeParticleQaSections';
import { SubjectSpineBindingChip } from '../../components/SubjectSpineBindingChip';
import { useKnowledgeParticleSegmentChat } from '../../hooks/useKnowledgeParticleSegmentChat';
import { MathTextBlock } from '../../components/MathTextBlock';
import { ProxyImage } from '../../components/assets/ProxyImage';
import {
  kpWorkspaceService,
  type WorkspaceGraphBundle,
  type WorkspaceGraphEdge,
  type WorkspaceGraphNode,
} from '../../services/kpWorkspaceService';
import { assetService } from '../../services/assetService';
import type { Asset, KnowledgeParticle } from '../../types/domain';
import { getParticleTitle } from '../../utils/particleDisplay';
import { buildAssetPreviewUrl } from '../../utils/mediaUrl';
import { safeExtractText } from '../../utils/textHelper';
import { readParticleSourceAssetId } from '../../utils/embeddedImageMetadata';
import type { PasPracticeThemeLink } from '../../utils/pasAsset';
import { resolveImageAnchorPosition } from './graphAnchorUtils';

type FittedParticleItem = {
  edge: WorkspaceGraphEdge;
  particle: WorkspaceGraphNode;
  particleId: string;
  derivedCount: number;
};

type AssetFittedGraphViewProps = {
  assetId: string;
  asset?: Asset | null;
  className?: string;
  practiceThemeLinks?: Map<string, PasPracticeThemeLink>;
  onOpenPractice?: (particleId: string) => void;
  onToast?: (message: string) => void;
};

function readNodeLabel(node: WorkspaceGraphNode) {
  return safeExtractText(node.title) || node.title || '知识颗粒';
}

function buildFittedParticleItems(bundle: WorkspaceGraphBundle, assetId: string): FittedParticleItem[] {
  const nodeById = new Map(bundle.nodes.map((node) => [node.id, node]));
  const assetParticleEdges = bundle.edges.filter(
    (edge) => edge.relation === 'asset_particle' && edge.source === `asset:${assetId}`,
  );

  const derivedCountBySource = new Map<string, number>();
  for (const edge of bundle.edges) {
    if (edge.relation !== 'particle_derived') {
      continue;
    }
    derivedCountBySource.set(edge.source, (derivedCountBySource.get(edge.source) || 0) + 1);
  }

  return assetParticleEdges
    .map((edge) => {
      const particle = nodeById.get(edge.target);
      if (!particle || particle.kind !== 'particle') {
        return null;
      }
      const particleId = particle.particleId || particle.id.replace(/^particle:/, '');
      return {
        edge,
        particle,
        particleId,
        derivedCount: derivedCountBySource.get(particle.id) || 0,
      };
    })
    .filter((item): item is FittedParticleItem => Boolean(item))
    .sort((left, right) => readNodeLabel(left.particle).localeCompare(readNodeLabel(right.particle), 'zh-CN'));
}

export function AssetFittedGraphView({
  assetId,
  asset,
  className = '',
  practiceThemeLinks,
  onOpenPractice,
  onToast,
}: AssetFittedGraphViewProps) {
  const [bundle, setBundle] = useState<WorkspaceGraphBundle | null>(null);
  const [particlesById, setParticlesById] = useState<Record<string, KnowledgeParticle>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedParticleId, setSelectedParticleId] = useState<string | null>(null);
  const [expandedParticleId, setExpandedParticleId] = useState<string | null>(null);
  const [imageZoomOpen, setImageZoomOpen] = useState(false);
  const segmentChat = useKnowledgeParticleSegmentChat({
    entrypoint: 'kp-graph',
    surface: 'kp_graph',
    onParticleUpdated: (updated) => {
      setParticlesById((prev) => ({ ...prev, [updated.id]: updated }));
    },
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [nextBundle, particles] = await Promise.all([
        kpWorkspaceService.getAssetWorkspaceGraph(assetId),
        assetService.listKnowledgeParticlesByAsset(assetId).catch(() => [] as KnowledgeParticle[]),
      ]);
      setBundle(nextBundle);
      const nextParticlesById: Record<string, KnowledgeParticle> = {};
      for (const particle of particles) {
        nextParticlesById[particle.id] = particle;
      }
      setParticlesById(nextParticlesById);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : '加载适配屏幕视图失败');
    } finally {
      setLoading(false);
    }
  }, [assetId]);

  useEffect(() => {
    void load();
  }, [load]);

  const fittedItems = useMemo(
    () => (bundle ? buildFittedParticleItems(bundle, assetId) : []),
    [assetId, bundle],
  );

  const previewUrl = useMemo(() => {
    const fromBundle = bundle?.root.surface?.previewUrl;
    if (fromBundle) {
      return buildAssetPreviewUrl(fromBundle) || fromBundle;
    }
    if (asset?.url) {
      return buildAssetPreviewUrl(asset.url) || asset.url;
    }
    return null;
  }, [asset?.url, bundle?.root.surface?.previewUrl]);

  const listItems = fittedItems;

  function handleSelectParticle(particleId: string, nodeId: string) {
    setSelectedParticleId(particleId);
    setExpandedParticleId((current) => (current === nodeId ? null : nodeId));
  }

  function clearSelection() {
    setSelectedParticleId(null);
    setExpandedParticleId(null);
  }

  if (loading && !bundle) {
    return (
      <div className={`flex min-h-[420px] items-center justify-center rounded-2xl border border-border bg-surface ${className}`}>
        <div className="flex flex-col items-center gap-3 text-sm text-muted">
          <Loader2 className="animate-spin text-primary" size={28} />
          正在加载适配屏幕视图，请稍候…
        </div>
      </div>
    );
  }

  if (error && !bundle) {
    return (
      <div className={`rounded-2xl border border-danger/30 bg-danger/5 p-5 text-sm text-danger ${className}`}>
        {error}
        <button type="button" onClick={() => void load()} className="ml-3 underline">重试</button>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="overflow-hidden rounded-2xl border border-border bg-[#EAF2FF]/35 shadow-card">
        <div className="flex items-center justify-between gap-3 border-b border-border/70 px-4 py-3">
          <div>
            <div className="text-sm font-semibold text-ink">适配屏幕视图</div>
            <div className="text-xs text-muted">{fittedItems.length} 个知识颗粒</div>
          </div>
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex items-center gap-1 rounded-lg border border-border bg-surface px-3 py-1.5 text-xs text-muted hover:text-primary"
          >
            <RefreshCw size={14} />
            刷新
          </button>
        </div>

        <div className="flex justify-center bg-[#EDF1F7] p-3 sm:p-4">
          <div className="relative w-full max-w-[720px]">
            <div className="relative aspect-[4/5] w-full overflow-hidden rounded-xl bg-surface shadow-inner">
              {previewUrl ? (
                <>
                  <button type="button" onClick={clearSelection} className="absolute inset-0 z-0">
                    <ProxyImage
                      rawUrl={previewUrl}
                      ossId={asset?.oss_id}
                      alt={bundle?.root.title || '素材预览'}
                      className="h-full w-full"
                      imgClassName="object-contain"
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => setImageZoomOpen(true)}
                    className="absolute bottom-3 right-3 z-20 inline-flex items-center gap-1 rounded-full bg-black/55 px-3 py-1 text-xs text-white"
                  >
                    <ZoomIn size={14} />
                    放大
                  </button>
                </>
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-muted">
                  暂时没有可渲染预览
                </div>
              )}

              {fittedItems.map((item, index) => {
                const selected = selectedParticleId === item.particleId;
                const position = resolveImageAnchorPosition(item.edge.anchor, index, fittedItems.length);
                return (
                  <button
                    key={item.edge.id}
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleSelectParticle(item.particleId, item.particle.id);
                    }}
                    className={`absolute z-10 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 transition ${
                      selected
                        ? 'h-5 w-5 border-primary bg-primary shadow-[0_0_0_4px_rgba(47,111,237,0.25)]'
                        : 'h-4 w-4 border-white bg-[#D97B7B] shadow'
                    }`}
                    style={{
                      left: `${position.x * 100}%`,
                      top: `${position.y * 100}%`,
                    }}
                    aria-label={readNodeLabel(item.particle)}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {listItems.length === 0 ? (
          <div className="rounded-2xl border border-border bg-surface p-5 text-sm text-muted">
            当前素材还没有关联的知识颗粒。
          </div>
        ) : null}

        {listItems.map((item) => {
          const selected = selectedParticleId === item.particleId;
          const expanded = expandedParticleId === item.particle.id;
          const particleRecord = particlesById[item.particleId];
          const title = particleRecord ? getParticleTitle(particleRecord) : readNodeLabel(item.particle);
          const subtitle = safeExtractText(item.particle.subtitle) || particleRecord?.subject || '';

          const particleMetadata = particleRecord?.metadata && typeof particleRecord.metadata === 'object'
            ? particleRecord.metadata as Record<string, unknown>
            : null;

          return (
            <div key={item.edge.id} className="overflow-hidden rounded-2xl border border-border bg-surface shadow-card">
              <div
                className={`flex w-full items-start gap-3 px-4 py-3 text-left transition ${
                  selected ? 'bg-primary/5' : 'hover:bg-canvas'
                }`}
              >
                <button
                  type="button"
                  onClick={() => handleSelectParticle(item.particleId, item.particle.id)}
                  className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#EAF2FF] text-primary"
                  aria-label={title}
                >
                  <Hexagon size={16} />
                </button>
                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() => handleSelectParticle(item.particleId, item.particle.id)}
                    className="w-full text-left"
                  >
                    <MathTextBlock text={title} className="text-sm font-semibold text-ink" />
                    {subtitle ? <p className="mt-1 text-xs text-muted">{subtitle}</p> : null}
                    {item.derivedCount > 0 ? (
                      <p className="mt-1 text-xs text-primary">衍生 {item.derivedCount}</p>
                    ) : null}
                  </button>
                  {practiceThemeLinks?.has(item.particleId) && onOpenPractice ? (
                    <button
                      type="button"
                      onClick={() => onOpenPractice(item.particleId)}
                      className="mt-2 inline-flex items-center gap-1 rounded-md px-0 py-0.5 text-xs font-medium text-primary hover:underline"
                    >
                      <Sparkles size={12} />
                      刻意练习
                      {(() => {
                        const link = practiceThemeLinks.get(item.particleId);
                        const count = link?.practiceItemCount || 0;
                        return count > 0 ? ` · ${count}` : '';
                      })()}
                    </button>
                  ) : null}
                  <div className="mt-2 flex justify-end">
                    <SubjectSpineBindingChip
                      particleId={item.particleId}
                      metadata={particleMetadata}
                      onToast={onToast}
                      onParticleUpdated={(updated) => {
                        setParticlesById((prev) => ({ ...prev, [updated.id]: updated }));
                      }}
                    />
                  </div>
                </div>
              </div>

              {expanded ? (
                <div className="border-t border-border bg-canvas/70 px-4 py-4">
                  {particleRecord ? (
                    <div className="space-y-4">
                      <KnowledgeParticleEmbeddedImageSection
                        particle={particleRecord}
                        preferredSource={
                          previewUrl
                            ? {
                                sourceAssetId: assetId,
                                sourceImageUrl: previewUrl,
                                ossId: asset?.oss_id || undefined,
                              }
                            : null
                        }
                        onParticleUpdated={(updated) => {
                          setParticlesById((prev) => ({ ...prev, [updated.id]: updated }));
                        }}
                      />
                      <KnowledgeParticleQaSections
                        particle={particleRecord}
                        segmentSelection={segmentChat.getSegmentSelection(particleRecord)}
                        fallbackQuestionText={title}
                      />
                      <KnowledgeParticlePapPanel
                        particleId={item.particleId}
                        renderFooterActions={(ctx) => (
                          <KnowledgeParticleOpsMoreMenu
                            particleId={item.particleId}
                            subject={particleRecord.subject}
                            sourceAssetId={readParticleSourceAssetId(
                              particleRecord.metadata && typeof particleRecord.metadata === 'object'
                                ? particleRecord.metadata as Record<string, unknown>
                                : null,
                            )}
                            papExtracting={ctx.extracting || ctx.running}
                            showPapExtract={ctx.eligible && !ctx.hasContent && !ctx.running}
                            showPapReExtract={ctx.eligible && (ctx.hasContent || ctx.failed)}
                            onExtractPap={() => { void ctx.extract(false); }}
                            onReExtractPap={() => { void ctx.extract(true); }}
                            onDeleted={() => {
                              setParticlesById((prev) => {
                                const next = { ...prev };
                                delete next[item.particleId];
                                return next;
                              });
                              setExpandedParticleId(null);
                              void load();
                            }}
                          />
                        )}
                      />
                    </div>
                  ) : (
                    <div className="space-y-3 text-sm">
                      {item.particle.question ? (
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-wide text-muted">问题</div>
                          <MathTextBlock text={safeExtractText(item.particle.question)} className="mt-1 text-ink" />
                        </div>
                      ) : null}
                      {item.particle.answer ? (
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-wide text-muted">答案</div>
                          <MathTextBlock text={safeExtractText(item.particle.answer)} className="mt-1 text-ink" />
                        </div>
                      ) : null}
                      <div className="flex justify-end">
                        <KnowledgeParticleOpsMoreMenu
                          particleId={item.particleId}
                          subject={
                            typeof item.particle.metadata?.subject === 'string'
                              ? item.particle.metadata.subject
                              : null
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {error ? <p className="text-sm text-danger">{error}</p> : null}

      {imageZoomOpen && previewUrl ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setImageZoomOpen(false)}
        >
          <div className="max-h-full max-w-5xl" onClick={(event) => event.stopPropagation()}>
            <ProxyImage
              rawUrl={previewUrl}
              ossId={asset?.oss_id}
              alt={bundle?.root.title || '素材预览'}
              className="max-h-[85vh] rounded-xl"
              imgClassName="object-contain"
            />
          </div>
        </div>
      ) : null}
      {segmentChat.overlays}
    </div>
  );
}
